import { Link, useNavigate } from 'react-router-dom';
import { useContext, useEffect, useState } from "react";
import UserContext from "../context/UserContext.js";
import { confirmPasswordReset, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup, verifyPasswordResetCode } from "firebase/auth";
import { auth, provider } from '../firebase/config.js';
import { Toaster, toast } from '../components/Toast.jsx';
import AuthLayout from '../components/landing/AuthLayout.jsx';
import GoogleButton from '../components/landing/GoogleButton.jsx';


const RECUPERAR_URL = "https://n8n.miqrapp.com/webhook/miqrapp/recuperar";

const Login = () => {

  const navigate = useNavigate();

  const { setUser} = useContext(UserContext);

  const LSuser = JSON.parse(window.localStorage.getItem("usuario"));

  // Link del mail de Firebase: /login?mode=resetPassword&oobCode=...
  // (en Firebase > Authentication > Plantillas, la URL de acción apunta a /login).
  const params = new URLSearchParams(window.location.search);
  const resetCode = params.get("mode") === "resetPassword" ? params.get("oobCode") : null;


  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);
  // Recuperar contraseña: "login" | "reset" (pedir el mail) | "sent" (mail enviado)
  // | "checking" (validando el link) | "newpass" (elegir la nueva) | "badlink" (vencido o usado)
  const [mode, setMode] = useState(resetCode ? "checking" : "login");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNew, setConfirmNew] = useState("");

  const signalError = (message) => toast.error(message);

  const handleSubmit = (e) => {
    e.preventDefault()
    setSending(true)

    signInWithEmailAndPassword(auth, email, password)
  .then((userCredential) => {

    if(userCredential.user) {
      setUser(userCredential.user)
      window.localStorage.setItem("usuario", JSON.stringify(userCredential.user))
    }
    navigate("/cuenta")
  })
  .catch((error) => {
    const errorCode = error.code;

    if (errorCode === "auth/invalid-credential") {
      signalError("Email o contraseña inválidos.")
    } else {
      signalError("No pudimos iniciar sesión. Probá de nuevo en unos minutos.")
    }
    setSending(false)
  });

  }

  const handleReset = async (e) => {
    e.preventDefault()
    setSending(true)
    auth.languageCode = "es";

    // Primero n8n: manda nuestro mail desde contacto@miqrapp.com con link a /login.
    // Si no responde bien, se usa el mail de Firebase como respaldo.
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 12000);
      const response = await fetch(RECUPERAR_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
        signal: controller.signal
      });
      clearTimeout(timer);
      if (response.ok) {
        setMode("sent")
        setSending(false)
        return
      }
    } catch (error) {
      console.error(error)
    }

    try {
      try {
        // Después de cambiar la contraseña, el botón "Continuar" vuelve a este login.
        await sendPasswordResetEmail(auth, email.trim(), { url: `${window.location.origin}/login` });
      } catch (error) {
        // Si el dominio no está autorizado en Firebase, se manda igual pero sin el link de vuelta.
        if (error.code !== "auth/unauthorized-continue-uri" && error.code !== "auth/invalid-continue-uri") throw error;
        await sendPasswordResetEmail(auth, email.trim());
      }
      setMode("sent")
    } catch (error) {
      if (error.code === "auth/user-not-found") {
        // No decimos si el mail tiene cuenta o no: mismo mensaje que si se hubiera enviado.
        setMode("sent")
      } else if (error.code === "auth/invalid-email") {
        signalError("Revisá el email, parece que está mal escrito.")
      } else if (error.code === "auth/too-many-requests") {
        signalError("Pediste muchos mails seguidos. Esperá unos minutos y probá de nuevo.")
      } else {
        console.error(error)
        signalError("No pudimos enviar el mail. Probá de nuevo en unos minutos.")
      }
    }
    setSending(false)
  }

  const handleGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      setUser(result.user);
      window.localStorage.setItem("usuario", JSON.stringify(result.user))

      navigate("/cuenta");
    } catch (error) {
      console.error(error);
      if (error.code === "auth/unauthorized-domain") {
        signalError("Este dominio no está habilitado para entrar con Google.")
      } else if (error.code === "auth/popup-blocked") {
        signalError("El navegador bloqueó la ventana de Google. Permití las ventanas emergentes y probá de nuevo.")
      } else if (error.code !== "auth/popup-closed-by-user" && error.code !== "auth/cancelled-popup-request") {
        signalError("No pudimos iniciar sesión con Google. Probá de nuevo.")
      }
    }
  }

  useEffect(() => {
    if (!resetCode) return;
    auth.languageCode = "es";
    verifyPasswordResetCode(auth, resetCode)
      .then((mail) => { setEmail(mail); setMode("newpass") })
      .catch(() => setMode("badlink"))
  }, [resetCode])

  const handleNewPassword = async (e) => {
    e.preventDefault()
    if (newPassword.length < 6) return signalError("La contraseña tiene que tener al menos 6 caracteres.")
    if (newPassword !== confirmNew) return signalError("Las contraseñas no coinciden.")
    setSending(true)

    try {
      await confirmPasswordReset(auth, resetCode, newPassword)
    } catch (error) {
      setSending(false)
      if (error.code === "auth/weak-password") return signalError("Contraseña muy débil. Debe tener al menos 6 caracteres.")
      if (error.code === "auth/expired-action-code" || error.code === "auth/invalid-action-code") return setMode("badlink")
      console.error(error)
      return signalError("No pudimos cambiar la contraseña. Probá de nuevo en unos minutos.")
    }

    // Contraseña cambiada: entra directo a la cuenta.
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, newPassword)
      setUser(user)
      window.localStorage.setItem("usuario", JSON.stringify(user))
      navigate("/cuenta", { replace: true })
    } catch {
      setSending(false)
      setPassword("")
      setMode("login")
      navigate("/login", { replace: true })
      toast.success("Listo, ya cambiaste tu contraseña. Ingresá con la nueva.")
    }
  }

  useEffect(() => {
    if (LSuser && !resetCode) {
      navigate("/cuenta")
    }
  }, [LSuser])
  
  return (
    <AuthLayout
      title="Iniciar sesión - MiQRapp"
      description="Entrá a tu cuenta de MiQRapp para cargar productos, cambiar precios o descargar el QR de tu catálogo."
      noindex
      eyebrow="Tu catálogo te espera"
      heading="¡Hola de nuevo!"
      subheading="Entrá para cargar productos, cambiar precios o descargar tu QR."
    >
      {mode === "login" && <>
        <h2>Ingresá a tu cuenta</h2>

        <GoogleButton onClick={handleGoogle}>Continuar con Google</GoogleButton>

        <div className="lp-auth-divider"><span>o con tu email</span></div>

        <form className='lp-auth-form' onSubmit={handleSubmit}>

          <div className="lp-field">
            <label htmlFor="email-login">Email</label>
            <input name='email-login' id='email-login' type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)}/>
          </div>

          <div className="lp-field">
            <div className="lp-field-row">
              <label htmlFor="login-password">Contraseña</label>
              <button type="button" className="lp-link-btn" onClick={() => setMode("reset")}>¿Olvidaste tu contraseña?</button>
            </div>
            <input type="password" name="login-password" id="login-password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}/>
          </div>

          <button className="lp-btn lp-btn-primary" type="submit" disabled={sending}>{sending ? "Ingresando..." : "Iniciar sesión"}</button>
        </form>

        <p className="lp-auth-switch">¿No tenés cuenta? <Link to="/register">Creala gratis</Link></p>
      </>}

      {mode === "reset" && <>
        <h2>Recuperá tu contraseña</h2>
        <p className="lp-auth-text">Escribí el email con el que te registraste y te mandamos un link para crear una contraseña nueva.</p>

        <form className='lp-auth-form' onSubmit={handleReset}>
          <div className="lp-field">
            <label htmlFor="email-reset">Email</label>
            <input name='email-reset' id='email-reset' type="email" autoComplete="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}/>
          </div>

          <button className="lp-btn lp-btn-primary" type="submit" disabled={sending}>{sending ? "Enviando..." : "Enviar link"}</button>
        </form>

        <p className="lp-auth-text lp-auth-small">Si entrás con Google no necesitás contraseña: usá "Continuar con Google".</p>
        <p className="lp-auth-switch"><button type="button" className="lp-link-btn lp-link-strong" onClick={() => setMode("login")}>Volver a iniciar sesión</button></p>
      </>}

      {mode === "checking" && <>
        <h2>Un segundo...</h2>
        <p className="lp-auth-text">Estamos revisando tu link.</p>
      </>}

      {mode === "newpass" && <>
        <h2>Elegí tu contraseña nueva</h2>
        <p className="lp-auth-text">Para la cuenta <strong>{email}</strong>.</p>

        <form className='lp-auth-form' onSubmit={handleNewPassword}>
          <input type="email" name="email" autoComplete="username" value={email} readOnly hidden/>
          <div className="lp-field">
            <label htmlFor="new-password">Contraseña nueva</label>
            <input type="password" id="new-password" name="new-password" autoComplete="new-password" minLength={6} maxLength={20} required autoFocus value={newPassword} onChange={(e) => setNewPassword(e.target.value)}/>
            <span className="lp-field-hint">Entre 6 y 20 caracteres.</span>
          </div>
          <div className="lp-field">
            <label htmlFor="new-password-confirm">Repetila</label>
            <input type="password" id="new-password-confirm" name="new-password-confirm" autoComplete="new-password" minLength={6} maxLength={20} required value={confirmNew} onChange={(e) => setConfirmNew(e.target.value)}/>
          </div>

          <button className="lp-btn lp-btn-primary" type="submit" disabled={sending}>{sending ? "Guardando..." : "Guardar y entrar"}</button>
        </form>
      </>}

      {mode === "badlink" && <>
        <h2>Este link ya no sirve</h2>
        <p className="lp-auth-text">Venció o ya se usó. Pedí uno nuevo y te lo mandamos al toque.</p>
        <div className="lp-auth-form">
          <button className="lp-btn lp-btn-primary" type="button" onClick={() => { navigate("/login", { replace: true }); setMode("reset") }}>Pedir otro link</button>
        </div>
        <p className="lp-auth-switch"><button type="button" className="lp-link-btn lp-link-strong" onClick={() => { navigate("/login", { replace: true }); setMode("login") }}>Volver a iniciar sesión</button></p>
      </>}

      {mode === "sent" && <>
        <h2>Revisá tu email</h2>
        <p className="lp-auth-text">Si <strong>{email.trim()}</strong> tiene una cuenta en MiQRapp, te llegó un mail con un link para crear una contraseña nueva. Puede tardar unos minutos.</p>
        <p className="lp-auth-text lp-auth-small">¿No lo ves? Fijate en spam o correo no deseado.</p>

        <div className="lp-auth-form">
          <button className="lp-btn lp-btn-primary" type="button" onClick={() => { setPassword(""); setMode("login") }}>Volver a iniciar sesión</button>
        </div>
        <p className="lp-auth-switch"><button type="button" className="lp-link-btn lp-link-strong" onClick={() => setMode("reset")}>Mandar de nuevo</button></p>
      </>}

      <Toaster></Toaster>
    </AuthLayout>
  )
}

export default Login
